import type { MailLogDelivery, MailLogStatus, MailSuppressionReason } from '@peles/shared';

/**
 * How the Mail log names and colours what it shows — one place, so the list,
 * the detail and the suppression list say the same thing about a message.
 */

export const STATUS_LABEL: Record<MailLogStatus, string> = {
  queued: 'Queued',
  sent: 'Sent',
  simulated: 'Simulated',
  suppressed: 'Not sent',
  failed: 'Failed',
};

/** `StatusBadge` colour names. */
export const STATUS_COLOR: Record<MailLogStatus, string> = {
  queued: 'gray',
  sent: 'green',
  simulated: 'gray',
  suppressed: 'orange',
  failed: 'red',
};

/** What each status means, for the tooltip — "simulated" alone reads as a success. */
export const STATUS_HINT: Record<MailLogStatus, string> = {
  queued: 'Logged, and not yet handed to the mail service — the send may have been interrupted.',
  sent: 'Accepted by the mail service.',
  simulated: 'Nothing was sent: email and texts are simulated outside production.',
  suppressed: 'Nothing was sent: every address had bounced or marked mail as spam before.',
  failed: 'The mail service refused it.',
};

export const DELIVERY_LABEL: Record<MailLogDelivery, string> = {
  delivered: 'Delivered',
  bounced: 'Bounced',
  complained: 'Marked as spam',
};

export const DELIVERY_COLOR: Record<MailLogDelivery, string> = {
  delivered: 'blue',
  bounced: 'red',
  complained: 'red',
};

export const REASON_LABEL: Record<MailSuppressionReason, string> = {
  bounce: 'Bounced',
  complaint: 'Marked as spam',
  manual: 'Added by hand',
};

export function formatWhen(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * A category as a person reads it: `guest-invite` → "Guest invite",
 * `thread.message` → "Thread message", `composed:vendor` → "Composed (vendor)".
 */
export function categoryLabel(category: string | null): string {
  if (!category) return 'Other';
  const [base, context] = category.split(':', 2);
  const words = (base ?? '').replace(/[-_.]+/g, ' ').trim();
  const label = words.charAt(0).toUpperCase() + words.slice(1);
  return context ? `${label} (${context})` : label;
}
